import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { CpuIcon, Volume2Icon, VolumeXIcon } from 'lucide-react';
import { DayTimeline } from './DayTimeline';
import { HouseModeChip } from '../ui/HouseModeChip';
import { BeheerButton } from '../ui/BeheerButton';
import { NoodstopButton } from '../ui/NoodstopButton';
import { useDb } from '../../hooks/useDb';
import { useSpecSettings } from '../../contexts/SpecSettingsContext';
import { euro } from '../../utils/time';
import type { DeskStage } from '../../hooks/useDeskStage';
const GPU_TONE = {
  slaapt: 'bg-ink-muted',
  'wordt gewekt': 'bg-amber',
  actief: 'bg-accent'
} as const;

/** Top: day band, house mode, cloud cost vs budget, GPU-brein, Beheer and Noodstop. */
export function DeskTopBar({
  stage
}: {
  stage: DeskStage;
}) {
  const {
    soundOn,
    setSoundOn
  } = useSpecSettings();
  const navigate = useNavigate();
  const meta = useDb(t => t.meta);
  const ratio = meta.cloud.spent / meta.cloud.budget;
  const warn = ratio >= 0.8 || meta.cloud.localOnly;
  return <header className="relative z-20 flex flex-wrap items-center gap-x-6 gap-y-3 border-b border-white/[0.06] px-6 pb-3 pt-14" data-id="element-625" data-mp="1f7anuspcsrah">
      <time className="font-display text-[34px] font-extralight leading-none tabular-nums text-ink" data-id="element-626" data-mp="1syxmgz6rqbwf">{meta.time}</time>
      <DayTimeline data-id="element-627" data-mp="1oydo5sagl28p" />
      <HouseModeChip mode={meta.mode} active data-id="element-628" data-mp="1oxvt2yzj8u2d" />

      <div className="w-[190px]" data-id="element-629" data-mp="116zwlok4taos">
        <div className="flex items-baseline justify-between gap-2 text-[13px]" data-id="element-630" data-mp="1181jcqaiflmc">
          <span className="text-ink-muted" data-id="element-631" data-mp="w9ghynjiuvwb">Cloud vandaag</span>
          <span className={`tabular-nums ${warn ? 'text-amber' : 'text-ink-soft'}`} data-id="element-632" data-mp="1a86cpwivcjhi">
            {euro(meta.cloud.spent)} / {euro(meta.cloud.budget)}
          </span>
        </div>
        <div className="relative mt-1.5 h-1.5 overflow-hidden rounded-full bg-white/[0.08]" role="meter" aria-valuemin={0} aria-valuemax={meta.cloud.budget} aria-valuenow={meta.cloud.spent} aria-label="Cloudkosten vandaag" data-id="element-633" data-mp="15bg1bmi0ec2f">
          <motion.div className={`h-full rounded-full ${warn ? 'bg-amber' : 'bg-accent'}`} initial={false} animate={{
          width: `${Math.min(100, ratio * 100)}%`
        }} transition={{
          duration: 0.3,
          ease: [0.23, 1, 0.32, 1]
        }} data-id="element-634" data-mp="1ecye4aa5mwxv" />
          <span aria-hidden className="absolute inset-y-0 left-[80%] w-px bg-white/40" data-id="element-635" data-mp="1sa3j06y4tprj" />
        </div>
        {meta.cloud.localOnly && <p className="mt-1 text-[13px] text-amber" data-id="element-636" data-mp="vijecig0r08z">Rest van de dag lokaal</p>}
      </div>

      <div className="flex items-center gap-2 whitespace-nowrap text-[13px] text-ink-soft" data-id="element-637" data-mp="zjv8n19u89ni">
        <CpuIcon className="h-4 w-4 text-ink-muted" aria-hidden data-id="element-638" data-mp="14jpzj4tm4z8k" />
        GPU-brein
        <span className={`h-2 w-2 rounded-full ${GPU_TONE[meta.gpu]}`} aria-hidden data-id="element-639" data-mp="19dc2u6dvzqrp" />
        <span className="text-ink" data-id="element-640" data-mp="bvbwubrkxeqo">{meta.gpu}</span>
      </div>

      <div className="ml-auto flex items-center gap-3" data-id="element-641" data-mp="gv6qgn2qoiff">
        <button type="button" onClick={() => setSoundOn(!soundOn)} aria-label={soundOn ? 'Geluid en stem uit' : 'Geluid en stem aan'} className="grid h-10 w-10 place-items-center rounded-full text-ink-muted transition-colors duration-150 hover:bg-white/[0.06] hover:text-ink" data-id="element-642" data-mp="gtna6wh327dn">
          {soundOn ? <Volume2Icon className="h-5 w-5" data-id="element-643" data-mp="3rhu0q1pegpjh" /> : <VolumeXIcon className="h-5 w-5" data-id="element-644" data-mp="3rginzcdz87g8" />}
        </button>
        <BeheerButton onClick={() => navigate('/beheer/logboek')} data-id="element-645" data-mp="3mi7betwariub" />
        <NoodstopButton onTrigger={stage.emergencyStop} data-id="element-646" data-mp="3mkk2pxny35k1" />
      </div>
    </header>;
}